/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback } from "react";
import PluginCard from "../ui/PluginCard";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import FormatDateTime from "../formatters/FormatDateTime";
import styled from "styled-components";
import { useToast } from "../ui/Toast";
import DiscountBadge from "./DiscountBadge";
import { DISCOUNT_TYPE } from "../../constants/constants";
import { useTranslate } from "../../hooks/useTranslate";
import Flex from "../ui/Flex";

const CodeLabel = styled.span`
  ${({ $inactive }) => $inactive && "text-decoration: line-through;"}
`;

const StyledDiscountBadge = styled(DiscountBadge)`
  align-self: start;
`;

const isExpired = (expiredDate) => {
  if (!expiredDate) return false;
  const expiredAt = new Date(expiredDate);

  return !Number.isNaN(expiredAt.getTime()) && expiredAt < new Date();
};

const ClientPromotionItem = ({ purchase }) => {
  const { toast } = useToast();
  const { _t } = useTranslate();
  const promotion = purchase.promotion || {};
  const expired = isExpired(purchase.expired_date);
  const isPercentage = promotion.discount_type === DISCOUNT_TYPE.PERCENTAGE;
  const itemIcon = isPercentage ? <Icon.Promotion size="90px" color="iconStroke" strokeWidth={1} /> : <Icon.GiftCard size="90px" strokeWidth={3}/>;

  const leftValue = purchase.can_be_used_count > 0
    ? purchase.can_be_used_count
    : "Unlimited";

  const includes = [
    {
      icon: <Icon.CalendarDetailed />,
      name: <>
        <FormatDateTime value={purchase.start_date} format={FormatDateTime.FORMAT.DATE} />
        {" - "}
        <FormatDateTime value={purchase.expired_date} format={FormatDateTime.FORMAT.DATE} />
      </>,
      label: 'Valid From - To'
    },
  ];

  if (isPercentage && purchase.can_be_used) {
    includes.push({
      icon: <Icon.FileList />,
      name: `${_t("Left:")} ${leftValue}`
    });
  }

  const handlePrint = () => {
    if (purchase.print_link && purchase.can_be_used) {
      window.open(purchase.print_link, "_blank");
    }
  };

  const handleCopy = useCallback(async () => {
    if (!purchase.can_be_used || !purchase.code) return;

    try {
      await navigator.clipboard.writeText(purchase.code);
      toast({
        description: "Promotion code has been copied",
        variant: "success",
      })
    } catch (e) {

    }
  }, [purchase.code, purchase.can_be_used, toast]);


  const controls = (
    <>
      <Button
        variant="outline"
        icon={purchase.can_be_used && <Icon.Files />}
        onClick={handleCopy}
        title={<CodeLabel $inactive={!purchase.can_be_used}>{purchase.code}</CodeLabel>}
      />
      <Button
        variant="primary"
        onClick={handlePrint}
        title={purchase.can_be_used ? "Print" : expired ? "Expired" : "Used"}
      />
    </>
  );


  const promotionModalFooter = (
    <Flex $direction="column" $gap="0.5rem">
      <StyledDiscountBadge
        discount={promotion.discount}
        discountType={promotion.discount_type}
        currency={promotion.currency}
      />
      {controls}
    </Flex>
  );

  return (
    <PluginCard
      disabled={!purchase.can_be_used}
      name={promotion.name}
      description={promotion.description}
      picture={promotion.picture_preview}
      fallbackIcon={itemIcon}
      beforeFooter={<DiscountBadge
        discount={promotion.discount}
        discountType={promotion.discount_type}
        currency={promotion.currency}
      />}
      includes={includes}
      footer={controls}
      modalFooter={promotionModalFooter}
    />
  );
};

export default ClientPromotionItem;
